import { useLazyQuery, useReactiveVar } from '@apollo/client'
import { DocumentTemplateViewContentItem } from '@deltek/specification-client/core/Models/documentTemplate'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { CircularProgress, Typography } from '@material-ui/core'
import { Theme, makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import AddCommentOutlined from '@material-ui/icons/AddCommentOutlined'
import AddCommentSharp from '@material-ui/icons/AddCommentSharp'
import CachedIcon from '@material-ui/icons/Cached'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import MoreVert from '@material-ui/icons/MoreVert'
import VisibilityOffSharpIcon from '@material-ui/icons/VisibilityOffSharp'
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined'
import { ProductTypeClashDetectionQuery } from 'api/graphQL/queries/product.queries'
import {
    ElementType,
    workspaceSelectedElement,
} from 'api/graphQL/reactiveVars/element'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import {
    isContentTeam,
    isOmWorkspaceUser,
    reactiveWorkspaceContents,
    workspaceContents,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { ProductTypeClashDetectionResponse } from 'api/graphQL/types/product.type'
import clsx from 'clsx'
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog'
import { TrackChangeType } from 'models/changes'
import { ProjectPageContext } from 'pages/ProjectsPage/ProjectPage/components/ProjectPageContext'
import React, { useMemo, useState } from 'react'
import {
    WORKSPACE_MANUFACTURER_SELECTED_KEY,
    WORKSPACE_NOTES_PARAMS,
    WorkspaceManufacturerSelected,
    WorkspaceNotesParameters,
    updateContentItemHiddenStatus,
} from 'shared/utils/workspaceUtils'

import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { BestPracticesIcons } from '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesIcons'
import { reactiveWorkspaceActions } from 'api/graphQL/reactiveVars/workspaceActions'
import { useIsBestPracticeTagDisabled } from 'shared/utils/bestPracticeUtils'

import { processEnv } from 'components/configuration/environmentVariables'
import {
    defaultColor,
    defaultOOFColor,
    draggingColor,
    errorColor,
    manufacturerSecondaryColor,
    manufacturerSecondaryColorOOFColor,
} from '../../../colorVars'
import {
    CompleteMenuItemConfig,
    WorkspacePopover,
} from '../../components/WorkspacePopover'
import { handleViewNotes } from '../ProjectNotesDialog/notesUtils'
import { WorkspaceContentContext } from '../WorkspaceContentContext'
import { AreaContext } from './AreaContext'
import { DRAGGABLE_ITEM_BORDER_RADIUS } from './DraggableItem'
import ProductTypeDeactivationWarning from './ProductTypeDeactivationWarning'
import SustainabilityWarningDialog from './SustainabilityWarningDialog'
import { NormalizedDocumentTemplateViewItem } from './model'
import {
    ProductTypeInFamilyViewContentType,
    isProductGroupIconShown,
    isProductTypeIconShown,
} from './utils'

export interface HoveredItemActionsProps {
    hovered: boolean
    setDraggableItemFocus?: (input: boolean) => void
    handleDialogOpen?: VoidFunction
    hasTitle?: boolean
    isDragging?: boolean
    titleHeight?: number
    className?: string
    onClickAdd?: () => void
    hasError?: boolean
    id?: string
    template?: NormalizedDocumentTemplateViewItem
    isHidden?: boolean
    isReviewer?: boolean
    isInProject?: boolean
    computedHeight?: number
    expanded?: boolean
    isPropertyUserAdded?: boolean
    isRemoveHidden?: boolean
    isPremiumPlus?: boolean
    isContentTeamUser?: boolean
    parent?: NormalizedDocumentTemplateViewItem
    contentTeamFromStudioPage?: boolean
    isDraftMode?: boolean
    onClickPills?: (ev: any) => void
    isPillsDialogOpen?: boolean
    deleteFamilyContent?: () => void
    onClickAddNoteSpecifier?: (
        selectedTempateData?: NormalizedDocumentTemplateViewItem
    ) => void
    isElementRemoved?: boolean
    disableArticleExpandInFullView?: boolean
    productTypeInFamilyViewContentType?: ProductTypeInFamilyViewContentType
    isUnderProductGroupArticle?: boolean
    handleBPMenuDisplay?: (
        event: React.MouseEvent<HTMLDivElement> | undefined
    ) => void
    isBPMenuDisplayed?: boolean
    isGlobalBestPracticeSynced?: boolean
    /**
     * Flag to indicate if Global Best Practice is currently syncing
     * Default is false
     **/
    isGlobalBestPracticeSyncing?: boolean
    /**
     * Flag to indicate if Global Best Practice is out of sync
     * Default is false
     **/
    isGlobalBestPracticeOutOfSync?: boolean
    /**
     * Flag to indicate if Best Practice is synced
     * Default is false
     **/
    isBestPracticeSynced?: boolean
    /**
     * Flag to indicate if Best Practice is currently syncing
     * Default is false
     **/
    isBestPracticeSyncing?: boolean
    /**
     * Flag to indicate if Best Practice is out of sync
     * Default is false
     **/
    isBestPracticeOutOfSync?: boolean
    /**
     * Handler for when out-of-sync icon is clicked
     * @param value "BP" or "GBP" to indicate which type was clicked
     */
    handleOutOfSyncIconClick?: (value: string) => void
    articleLoaded?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    container: ({
        hovered,
        hasTitle,
        titleHeight,
        isDragging,
        hasError,
        template,
        computedHeight,
        expanded,
        isPremiumPlus,
        productTypeInFamilyViewContentType,
    }: HoveredItemActionsProps) => {
        let backgroundColor = ''
        let borderBottomRightRadius = 0

        if (hasError) {
            backgroundColor = errorColor
        } else if (hovered) {
            backgroundColor = defaultColor
            if (isPremiumPlus) {
                backgroundColor = manufacturerSecondaryColor
            }
        } else if (hasTitle) {
            if (isDragging) {
                backgroundColor = draggingColor
            } else {
                backgroundColor = isProductGroupIconShown(
                    productTypeInFamilyViewContentType
                )
                    ? 'transparent'
                    : theme.palette.common.white
            }
        }

        if (!expanded) {
            borderBottomRightRadius = DRAGGABLE_ITEM_BORDER_RADIUS
        } else {
            borderBottomRightRadius = 0
        }

        return {
            position: 'absolute',
            top: 0,
            right: 0,
            width: 'auto',
            height: hasTitle && titleHeight ? computedHeight : 30,
            bottom:
                hasTitle && titleHeight && hovered && titleHeight > 40
                    ? 1
                    : 'auto',
            backgroundColor,
            display: 'block',
            fontSize: pxToRem(22),
            borderTopRightRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
            borderBottomRightRadius,
            color: template?.isHidden ? 'rgba(255,255,255,0.25)' : 'inherit',
            zIndex: 1000,
        }
    },
    fullHeight: (props: HoveredItemActionsProps) => ({
        height: '100%',
        paddingRight: theme.spacing(1.2),
    }),
    icons: {
        fontSize: pxToRem(22),
        margin: theme.spacing(0, 0.5),
        color: (props: HoveredItemActionsProps) =>
            !props.isInProject || props.isReviewer
                ? '#FFFFFF40'
                : theme.palette.common.white,
    },

    iconsInactive: {
        fontSize: pxToRem(22),
        margin: theme.spacing(0, 0.5),
        color: 'rgba(255,255,255,0.25)',
        cursor: 'default',
    },
    addIcon: {
        fontSize: pxToRem(22),
        margin: theme.spacing(0, 0.5),
        borderRadius: '15%',
        color: theme.palette.common.white,
    },
    addIconInactive: {
        color: 'rgba(255,255,255,0.25)',
        fontSize: pxToRem(22),
        margin: theme.spacing(0, 0.5),
        borderRadius: '15%',
        cursor: 'default',
    },
    trashCanIcon: {
        fontSize: pxToRem(26),
        margin: theme.spacing(0, 0.5),
        color: theme.palette.common.white,
        cursor: 'pointer',
        pointerEvents: 'auto',
    },
    trashCanIconInactive: {
        color: 'rgba(255,255,255,0.25)',
        fontSize: pxToRem(26),
        margin: theme.spacing(0, 0.5),
        cursor: 'default',
        pointerEvents: 'none',
    },
    iconSharp: {
        fontSize: pxToRem(22),
        margin: theme.spacing(0, 0.5),
        color: (props: HoveredItemActionsProps) =>
            !props.isInProject ? '#FFFFFF40' : theme.palette.common.white,
        cursor: 'pointer',
    },
    bestPractice: {
        position: 'absolute',
        bottom: '0',
        right: '0',
        height: 40,
        fontSize: pxToRem(24),
        color: theme.palette.common.white,
        display: 'flex',
        alignItems: 'center',
    },
    iconsTitle: (props: HoveredItemActionsProps) => ({
        fontSize: pxToRem(22),
        margin: theme.spacing(0, 0.5),
        cursor: 'pointer',
        color:
            props?.template?.isHidden ||
            props.isReviewer ||
            !props.isInProject ||
            props.contentTeamFromStudioPage
                ? defaultOOFColor
                : defaultColor,
    }),

    iconsInactiveTitle: (props: HoveredItemActionsProps) => ({
        fontSize: pxToRem(22),
        cursor: 'default',
        margin: theme.spacing(0, 0.5),
        color:
            props?.template?.isHidden ||
            props.isReviewer ||
            !props.isInProject ||
            props.contentTeamFromStudioPage
                ? defaultOOFColor
                : defaultColor,
    }),
    addIconTitle: (props: HoveredItemActionsProps) => {
        let color: string
        if (props.isPremiumPlus) {
            color = !props.isInProject || props?.template?.isHidden
                ? manufacturerSecondaryColorOOFColor
                : manufacturerSecondaryColor
        } else {
            color = props?.template?.isHidden ||
                !props.isInProject ||
                props.contentTeamFromStudioPage
                ? defaultOOFColor
                : props.template?.hasError
                ? theme.palette.common.white
                : defaultColor
        }
        return {
            fontSize: pxToRem(22),
            margin: theme.spacing(0, 0.5),
            borderRadius: '15%',
            color,
            cursor: 'pointer',
        }
    },
    addIconInactiveTitle: (props: HoveredItemActionsProps) => {
        let color: string
        if (props.isPremiumPlus) {
            color = !props.isInProject || props?.template?.isHidden
                ? manufacturerSecondaryColorOOFColor
                : manufacturerSecondaryColor
        } else {
            color = props?.template?.isHidden ||
                !props.isInProject ||
                props.contentTeamFromStudioPage
                ? defaultOOFColor
                : defaultColor
        }
        return {
            fontSize: pxToRem(22),
            margin: theme.spacing(0, 0.5),
            borderRadius: '15%',
            color,
            cursor: props.isReviewer ? 'pointer' : 'default',
        }
    },
    trashCanIconTitle: (props: HoveredItemActionsProps) => {
        let color: string
        if (props?.template?.isHidden ||
            !props.isInProject ||
            props.contentTeamFromStudioPage) {
            color = defaultOOFColor
        } else if (props.template?.hasError) {
            color = theme.palette.common.white
        } else {
            color = defaultColor
        }
        return {
            fontSize: pxToRem(26),
            margin: theme.spacing(0, 0.5),
            color,
            cursor: 'pointer',
        }
    },
    trashCanIconInactiveTitle: (props: HoveredItemActionsProps) => {
        let color: string
        if (props?.template?.isHidden ||
            props.isReviewer ||
            !props.isInProject ||
            props.contentTeamFromStudioPage) {
            color = defaultOOFColor
        } else {
            color = defaultColor
        }
        return {
            fontSize: pxToRem(26),
            margin: theme.spacing(0, 0.5),
            color,
            cursor: props.isReviewer ? 'pointer' : 'default',
        }
    },
    addButton: {
        cursor: 'pointer',
    },
    iconOutlinedTitle: {
        cursor: 'pointer',
    },
    gridItem: {
        minWidth: theme.spacing(4),
        lineHeight: 0,
        textAlign: 'center',
        fontSize: pxToRem(22),
    },
    BPIcons: {
        textAlign: 'center',
        color: '#10B091',
        position: 'absolute',
        left: -theme.spacing(4),
    },
    bpPosition: (props: HoveredItemActionsProps) => ({
        display: 'flex',
        color: !props.isInProject
            ? props?.hovered && !props.contentTeamFromStudioPage
                ? '#FFFFFF40'
                : props.isPremiumPlus
                ? manufacturerSecondaryColorOOFColor
                : defaultOOFColor
            : props?.hovered && !props?.template?.isDeactivated
            ? theme.palette.common.white
            : props?.template?.isDeactivated
            ? 'rgba(255,255,255,0.25)'
            : props.isPremiumPlus
            ? manufacturerSecondaryColor
            : props.contentTeamFromStudioPage
            ? defaultOOFColor
            : defaultColor,
        cursor: 'pointer',
    }),
    bpCursor: {
        cursor: 'pointer',
    },
    bpPostionisContentTeamUser: (props: HoveredItemActionsProps) => ({
        color: props?.hovered
            ? '#FFFFFF40'
            : props?.isPremiumPlus
            ? '#006958'
            : defaultColor,
    }),
    moreMenuLevelOfSpecificationAutomationIcon: (
        props: HoveredItemActionsProps
    ) => ({
        display: 'flex',
        color: !!props.hovered ? '#FFFFFF40' : defaultOOFColor,
        cursor: 'pointer',
    }),
    menuText: {
        fontSize: pxToRem(12),
        color: '#211F20',
        cursor: 'pointer',
        marginTop: -theme.spacing(0.5),
    },
    menuTextContent: {
        fontSize: pxToRem(12),
        color: '#211F20',
        cursor: 'pointer',
        paddingBottom: theme.spacing(0.5),
    },
    notesMenuText: {
        fontSize: pxToRem(12),
        color: '#211F20',
        paddingLeft: theme.spacing(1.875),
        cursor: 'pointer',
    },
    addPTButton: {
        height: '100%',
    },
    newPropertyLoading: {
        color: theme.palette.common.white,
    },
    newPropertyLoadingNonHover: {
        color: defaultColor,
    },
    newPTIconContainer: {
        backgroundColor: '#F78909',
        height: 'inherit',
        padding: theme.spacing(0.38, 0.63),
        '& .MuiIconButton-root': {
            padding: 0,
        },
    },
    avatar: {
        backgroundColor: theme.palette.common.white,
        color: '#F78909',
        height: theme.spacing(3.15),
        width: theme.spacing(3.15),
        fontSize: pxToRem(20),
    },
}))

export const HoveredItemActions: React.FC<HoveredItemActionsProps> = ({
    hovered,
    setDraggableItemFocus,
    hasTitle,
    isDragging,
    titleHeight,
    className,
    onClickAdd,
    hasError,
    id,
    template,
    isHidden,
    isReviewer,
    handleDialogOpen,
    expanded,
    isPropertyUserAdded,
    isRemoveHidden,
    contentTeamFromStudioPage,
    isDraftMode,
    onClickPills,
    parent,
    onClickAddNoteSpecifier,
    deleteFamilyContent,
    isElementRemoved,
    isPremiumPlus,
    disableArticleExpandInFullView,
    productTypeInFamilyViewContentType,
    isUnderProductGroupArticle,
    handleBPMenuDisplay,
    isBPMenuDisplayed,
    isBestPracticeOutOfSync = false,
    isBestPracticeSynced = false,
    isBestPracticeSyncing = false,
    isGlobalBestPracticeOutOfSync = false,
    isGlobalBestPracticeSynced = false,
    isGlobalBestPracticeSyncing = false,
    handleOutOfSyncIconClick,
    articleLoaded,
}) => {
    const {
        handleBestPracticeProperty,
        nugget,
        selectedParagraphItemId,
        type,
        projectId,
        getPersistedState,
        handleInActivateProperty,
    } = React.useContext(AreaContext)
    const { projectData } = React.useContext(ProjectPageContext)

    const serviceWorker = navigator.serviceWorker
    const isContentTeamUser = useReactiveVar(isContentTeam)
    const isOMUser = useReactiveVar(isOmWorkspaceUser)

    const { isAdmin, isSystemUser } = useReactiveVar(userInfo)
    const { selectedElementNode: selectedProjectNode } = useReactiveVar(
        workspaceSelectedElement
    )

    // use this flag as guard condition: don't show BP icon if project imported family, just to not confuse the user if for some reason it was tagged sa BP
    const isProjectImported = reactiveWorkspaceContents()?.isProjectImported

    const shouldShowBestPracticeIcon = useMemo(() => {
        // System-added content: Always show when expanded
        if (!template?.isUserAdded) {
            return true
        }

        // User-added content: Show if level 1 OR first user-added under system parent
        if (template?.isUserAdded) {
            const isTopLevelUserAdded = template?.level === 1
            const isFirstUserAddedUnderSystem = parent && !parent?.isUserAdded

            return (
                (isTopLevelUserAdded || isFirstUserAddedUnderSystem) && expanded
            )
        }

        return false
    }, [template?.isUserAdded, template?.level, parent?.isUserAdded, expanded])

    const isBPTagDisabledInput = {
        userInfo,
        isInProject: selectedProjectNode?.isInProject,
        contentTeamFromStudioPage,
        isUserAddedPR: template?.isUserAdded,
        isProjectImported,
        isProjectOnly: selectedProjectNode?.isProjectOnly,
    }

    // checking if BP tags are disabled for editing
    const isBestPracticeEditDisabled = useIsBestPracticeTagDisabled({
        ...isBPTagDisabledInput,
        isUserAddedPR:
            processEnv.REACT_APP_EnableSmartProjectCopy === '1'
                ? template?.isUserAdded && shouldShowBestPracticeIcon
                    ? false
                    : template?.isUserAdded
                : isBPTagDisabledInput.isUserAddedPR,
    })

    const isFromPPLTemplate = !!selectedProjectNode?.isFromPPLTemplate

    // checking if BP tags are disabled for viewing
    const isBestPracticeViewDisabled = useIsBestPracticeTagDisabled({
        ...isBPTagDisabledInput,
        isUserAddedPR:
            processEnv.REACT_APP_EnableSmartProjectCopy === '1'
                ? template?.isUserAdded && shouldShowBestPracticeIcon
                    ? false
                    : template?.isUserAdded
                : isBPTagDisabledInput.isUserAddedPR,
        isViewOnly: true,
    })

    const { showSMMActionButtons } = reactiveWorkspaceActions()

    const [isMainMenuDisplayed, setIsMainMenuDisplayed] = React.useState<
        boolean
    >(false)

    const [isNotesMenuDisplayed, setIsNotesMenuDisplayed] = React.useState<
        boolean
    >(false)

    const handleClickAddSpecifierNote = (
        event: React.MouseEvent<SVGSVGElement>
    ) => {
        if (isOMUser || !isAdmin) onClickAddNoteSpecifier?.(template)
    }
    const [
        isSustainabilityWarningDisplayed,
        setIsSustainabilityWarningDisplayed,
    ] = React.useState<boolean>(false)
    const isProductFamily = useMemo(() => {
        return selectedProjectNode?.elementType === 'PRODUCTFAMILY'
    }, [])
    const isProductType = useMemo(() => {
        return selectedProjectNode?.elementType === 'PRODUCTTYPE'
    }, [])

    // Clash Detection Related Variables
    const [showClashDialog, setClashDialog] = React.useState<boolean>(false)

    // prohibit deactivation of Product Type content in family view
    const [
        isProductTypeDeactivationDialogOpen,
        setProductTypeDeactivationDialogOpen,
    ] = useState(false)

    const [
        clashedDetectionResponse,
        setClashedDetectionResponse,
    ] = React.useState<ProductTypeClashDetectionResponse>()

    const actionRef = React.useRef<HTMLDivElement>()
    const isBestPractice: boolean = React.useMemo(() => {
        return template?.contentItem?.isBestPractice ?? false
    }, [template])

    const isGlobalBestPractice: boolean = React.useMemo(() => {
        return template?.contentItem?.isGlobalBestPractice ?? false
    }, [template])
    const isArticle: boolean = React.useMemo(() => {
        return template?.contentItem?.textType === 'Article'
    }, [template])
    const isPillsDialogOpen: boolean = React.useMemo(() => {
        return selectedParagraphItemId === template?.itemId
    }, [selectedParagraphItemId, template])

    const { contentItemWorker } = React.useContext(WorkspaceContentContext)

    const isDeleteDisabled: boolean = React.useMemo(() => {
        const areElementsDeleted =
            template?.contentItem?.elements?.every(
                (element) =>
                    element.changeType?.toLowerCase() ===
                    TrackChangeType.DELETED.toLowerCase()
            ) ?? false

        const isElementProductTypeInSMM =
            (contentTeamFromStudioPage && template?.isProductType) ?? false

        return (
            template?.isProductGroup ||
            template?.isProductGroupHasMultipleProductTypes ||
            areElementsDeleted ||
            isElementProductTypeInSMM
        )
    }, [template])

    const computedHeight =
        (hovered || isPillsDialogOpen) && titleHeight && titleHeight > 40
            ? titleHeight - 1
            : titleHeight

    const classes = useStyles({
        hovered: hovered || isPillsDialogOpen,
        hasTitle,
        isDragging,
        titleHeight,
        hasError,
        template,
        isReviewer,
        isInProject: selectedProjectNode?.isInProject,
        computedHeight,
        expanded,
        isPropertyUserAdded,
        isRemoveHidden,
        isContentTeamUser: isContentTeamUser || isOMUser,
        contentTeamFromStudioPage,
        isPremiumPlus,
        productTypeInFamilyViewContentType,
    })

    const { isLevelOfSpecificationAutomation } = useReactiveVar(
        workspaceSelectedElement
    )

    const handleElementDeactivation = React.useCallback(() => {
        if (isSustainabilityWarningDisplayed) {
            setIsSustainabilityWarningDisplayed(false)
        }
        const { familyAddedProductTypes, dtv } = workspaceContents()

        updateContentItemHiddenStatus(
            template,
            selectedProjectNode,
            familyAddedProductTypes,
            projectId as string,
            handleInActivateProperty,
            nugget,
            hasTitle,
            undefined,
            undefined,
            undefined,
            contentTeamFromStudioPage && isOMUser
        )

        if (template?.isBOD || template?.isManufacturers) {
            const contentItem =
                template?.documentTemplateViewContents?.[0]?.documentTemplateViewContentItems?.[0] ??
                template?.documentTemplateViewItem?.documentTemplateViewContents?.[0]?.documentTemplateViewContentItems?.find(
                    (ci) => ci.id === template?.contentItem?.id
                )
            if (contentItem?.isHidden === true) {
                const opposingDTV = template?.isBOD
                    ? dtv?.items?.find((item) => item.isManufacturers)
                    : dtv?.items?.find((item) => item.isBOD)
                if (
                    opposingDTV?.documentTemplateViewContents?.some((c) =>
                        c.documentTemplateViewContentItems?.some(
                            (ci) => !ci.isHidden
                        )
                    )
                ) {
                    updateContentItemHiddenStatus(
                        opposingDTV,
                        selectedProjectNode,
                        familyAddedProductTypes,
                        projectId as string,
                        handleInActivateProperty,
                        nugget,
                        undefined,
                        undefined,
                        dtv,
                        true
                    )
                }
            }
        }

        const isFamily = selectedProjectNode?.elementType === 'PRODUCTFAMILY'

        if (!isFamily) {
            if (template?.isManufacturers || template?.isBOD) {
                // send serviceWorker change
                serviceWorker?.controller?.postMessage({
                    [WORKSPACE_MANUFACTURER_SELECTED_KEY]: {
                        listingId: '',
                        isSelected: false,
                        isBOD: true,
                        removeSelected: true,
                    } as WorkspaceManufacturerSelected,
                })
            }
        }
        setClashDialog(false)
        setDraggableItemFocus?.(false)
    }, [
        template,
        selectedProjectNode,
        projectId,
        hasTitle,
        nugget,
        serviceWorker,
        handleInActivateProperty,
        nugget,
        isSustainabilityWarningDisplayed,
        setDraggableItemFocus,
    ])

    const closeProductDeactivationDialog = React.useCallback(() => {
        setProductTypeDeactivationDialogOpen(false)
    }, [])

    const closeClashDialog = React.useCallback(() => {
        setClashDialog(false)
        setDraggableItemFocus?.(false)
    }, [setDraggableItemFocus])

    //disable inactivation for Product Type article or product type paragraph
    const disableProductTypeInactivation = React.useMemo(() => {
        if (contentTeamFromStudioPage) return
        const isProductTypeRestrictedContent =
            template?.contentItem?.textType === 'Article' ||
            template?.isProductType

        const isProductTypeContentOnFamilyView =
            isProductTypeIconShown(productTypeInFamilyViewContentType) ||
            isProductGroupIconShown(productTypeInFamilyViewContentType)

        return selectedProjectNode?.elementType === 'PRODUCTTYPE'
            ? isProductTypeRestrictedContent &&
                  !template?.isSharedProductTypeProperty
            : isProductTypeContentOnFamilyView
    }, [
        productTypeInFamilyViewContentType,
        template,
        contentTeamFromStudioPage,
        selectedProjectNode,
    ])

    const clashDialogContent = React.useMemo(() => {
        return (
            <>
                {clashedDetectionResponse?.clashDetection.message}
                <ul style={{ color: 'black' }}>
                    {clashedDetectionResponse?.clashDetection?.clashedProductTypes?.map(
                        (item, index) => {
                            return (
                                <li key={index}>
                                    {item.productType}
                                    {item.productTypeParentAssemblies
                                        ? ` (${item.productTypeParentAssemblies.join(
                                              ', '
                                          )})`
                                        : ''}
                                </li>
                            )
                        }
                    )}
                </ul>
            </>
        )
    }, [clashedDetectionResponse])

    const [clashDetectionAPICall] = useLazyQuery<
        ProductTypeClashDetectionResponse
    >(ProductTypeClashDetectionQuery, {
        fetchPolicy: 'no-cache',
        displayName: 'ClashedDetectionQuery',
        onCompleted: React.useCallback(
            (data: ProductTypeClashDetectionResponse) => {
                if (data?.clashDetection?.clashedProductTypes?.length !== 0) {
                    setClashedDetectionResponse(data)
                    setClashDialog(true)
                } else {
                    setDraggableItemFocus?.(false)
                    handleElementDeactivation()
                }
            },
            [handleElementDeactivation, setDraggableItemFocus]
        ),
        onError: (error) => {
            setDraggableItemFocus?.(false)
            handleElementDeactivation()
        },
    })

    const checkActionPrerequisites = React.useCallback(() => {
        if (!selectedProjectNode?.isInProject) {
            return
        }
        const documentTemplateViewContentItem:
            | Partial<DocumentTemplateViewContentItem>
            | undefined =
            template?.documentTemplateViewContents?.[0]
                ?.documentTemplateViewContentItems?.[0] ??
            template?.documentTemplateViewItem?.documentTemplateViewContents?.[0]?.documentTemplateViewContentItems?.find(
                (dtvContentItem: Partial<DocumentTemplateViewContentItem>) =>
                    dtvContentItem.id === template?.contentItem?.id
            )

        if (disableProductTypeInactivation) {
            setProductTypeDeactivationDialogOpen(true)
            return
        }

        if (
            documentTemplateViewContentItem?.isSustainabilityContentItem &&
            !documentTemplateViewContentItem?.isSelectedSustainabilityContentItem &&
            documentTemplateViewContentItem?.isHidden
        ) {
            setIsSustainabilityWarningDisplayed(true)
        } else {
            setDraggableItemFocus?.(true)
            clashDetectionAPICall({
                variables: {
                    input: {
                        documentTemplateViewId:
                            template?.documentTemplateView?.id,
                        projectId,
                        documentTemplateViewItemId: template?.id,
                        documentTemplateViewItemHiddenStatus: !isHidden,
                        isArticle,
                    },
                },
            })
        }
    }, [
        selectedProjectNode,
        template,
        projectId,
        isReviewer,
        isArticle,
        clashDetectionAPICall,
        isHidden,
        setDraggableItemFocus,
        disableProductTypeInactivation,
    ])

    const cancelAction = React.useCallback(() => {
        setIsSustainabilityWarningDisplayed(false)
    }, [])

    const handleNotesMenuDisplay = React.useCallback(() => {
        setIsNotesMenuDisplayed((past) => !past)
    }, [])

    const handleViewNotesDialog = React.useCallback(
        (isAddNote: boolean = false) => {
            const isNotesDialogOpened = getPersistedState?.(
                'projectNotesDialogOpened'
            )
            if (!isNotesDialogOpened) {
                handleViewNotes({
                    getPersistedState: getPersistedState,
                    projectId: projectId!,
                    selectedProjectElementNode: selectedProjectNode,
                })
            }
            const { projectProductFamilyId } = workspaceContents()
            const defaultNotesParams = {
                elementId: selectedProjectNode?.elementId as string,
                projectProductFamilyId: projectProductFamilyId as string,
            }

            const notesParams: Partial<WorkspaceNotesParameters> = !isAddNote
                ? defaultNotesParams
                : selectedProjectNode?.elementType !== 'PRODUCTTYPE'
                ? {
                      ...defaultNotesParams,
                      contentItemId: template?.contentItem?.id as string,
                      isAddNote: true,
                  }
                : {
                      ...defaultNotesParams,
                      contentItemId: template?.contentItem?.id as string,
                      isAddNote: true,
                      ptNotesProperties: {
                          isProductTypeProperty: template?.isForProductType as boolean,
                      },
                  }
            setTimeout(
                () => {
                    serviceWorker?.controller?.postMessage({
                        [WORKSPACE_NOTES_PARAMS]: {
                            ...notesParams,
                        } as WorkspaceNotesParameters,
                    })
                },
                !isNotesDialogOpened ? 3000 : 0
            )
        },
        [
            getPersistedState,
            projectId,
            selectedProjectNode,
            serviceWorker,
            template,
        ]
    )

    const mainMenuItems: CompleteMenuItemConfig[] = React.useMemo(
        () =>
            !isBestPracticeEditDisabled && !isFromPPLTemplate && !isPillsDialogOpen
                ? [
                      {
                          dataTestId: 'bestPractice',
                          label: 'Best Practices',
                          component: (
                              <GridContainer
                                  justifyContent="space-between"
                                  alignItems="center"
                                  onClick={handleBPMenuDisplay}
                                  key={'bpMenu'}
                              >
                                  <GridItem>
                                      <Typography
                                          component="span"
                                          className={classes.menuText}
                                      >
                                          Best Practices
                                      </Typography>
                                  </GridItem>
                                  <GridItem style={{ maxHeight: pxToRem(24) }}>
                                      <ChevronRightIcon
                                          fontSize="small"
                                          htmlColor="#858585"
                                          className={classes.bpCursor}
                                      />
                                  </GridItem>
                              </GridContainer>
                          ),
                      },
                      {
                          dataTestId: 'notes',
                          label: 'Notes',
                          component: (
                              <GridContainer
                                  justifyContent="space-between"
                                  alignItems="center"
                                  onClick={handleNotesMenuDisplay}
                                  key={'notesMenu'}
                              >
                                  <GridItem>
                                      <Typography
                                          component="span"
                                          className={classes.menuText}
                                      >
                                          Notes
                                      </Typography>
                                  </GridItem>
                                  <GridItem style={{ maxHeight: pxToRem(24) }}>
                                      <ChevronRightIcon
                                          fontSize="small"
                                          htmlColor="#858585"
                                      />
                                  </GridItem>
                              </GridContainer>
                          ),
                      },
                  ]
                : [
                      {
                          dataTestId: 'notes',
                          label: 'Notes',
                          component: (
                              <GridContainer
                                  justifyContent="space-between"
                                  alignItems="center"
                                  onClick={handleNotesMenuDisplay}
                                  key={'notesMenu'}
                              >
                                  <GridItem>
                                      <Typography
                                          component="span"
                                          className={classes.menuText}
                                      >
                                          Notes
                                      </Typography>
                                  </GridItem>
                                  <GridItem style={{ maxHeight: pxToRem(24) }}>
                                      <ChevronRightIcon
                                          fontSize="small"
                                          htmlColor="#858585"
                                      />
                                  </GridItem>
                              </GridContainer>
                          ),
                      },
                  ],
        [
            handleNotesMenuDisplay,
            classes,
            isBestPracticeEditDisabled,
            isFromPPLTemplate,
            isPillsDialogOpen,
            isBestPractice,
            isGlobalBestPractice,
            projectData,
            contentTeamFromStudioPage,
            isAdmin,
            template,
            selectedProjectNode,
            isBPMenuDisplayed,
        ]
    )

    const notesMenuItems: CompleteMenuItemConfig[] = React.useMemo(
        () => [
            {
                dataTestId: 'backToMain',
                label: 'Main Menu',
                component: (
                    <GridContainer
                        justifyContent="flex-start"
                        alignItems="center"
                        onClick={handleNotesMenuDisplay}
                        key={'backToMain'}
                    >
                        <GridItem
                            style={{
                                maxHeight: pxToRem(24),
                                left: pxToRem(3),
                                position: 'absolute',
                            }}
                        >
                            <ChevronLeftIcon
                                fontSize="small"
                                htmlColor="#858585"
                            />
                        </GridItem>
                        <GridItem style={{ paddingLeft: 15 }}>
                            <Typography
                                component="span"
                                className={classes.menuText}
                            >
                                Main Menu
                            </Typography>
                        </GridItem>
                    </GridContainer>
                ),
            },
            {
                dataTestId: 'allNotes',
                label: 'All Notes',
                onClick: () => handleViewNotesDialog(),
            },
            {
                dataTestId: 'createNote',
                label: 'Create New Note',
                onClick: () => handleViewNotesDialog(true),
            },
        ],
        [handleNotesMenuDisplay, handleViewNotesDialog, classes] // eslint-disable-line
    )

    const eyeIconColor = React.useMemo(() => {
        if (isLevelOfSpecificationAutomation) {
            return defaultColor
        }

        if (disableArticleExpandInFullView) {
            return defaultOOFColor
        }

        if (isPremiumPlus && !selectedProjectNode?.isInProject) {
            return manufacturerSecondaryColorOOFColor
        }

        if (isPremiumPlus) {
            return manufacturerSecondaryColor
        }

        if (!selectedProjectNode?.isInProject) {
            return defaultOOFColor
        }

        return defaultColor
    }, [
        isPremiumPlus,
        isReviewer,
        selectedProjectNode,
        disableArticleExpandInFullView,
        isLevelOfSpecificationAutomation,
    ])

    const activeEyeColor = React.useMemo(() => {
        if (disableArticleExpandInFullView) {
            return defaultOOFColor
        }
        if (hasError) {
            return '#FFFFFF'
        }
        return eyeIconColor
    }, [
        isPremiumPlus,
        selectedProjectNode,
        isReviewer,
        hasError,
        disableArticleExpandInFullView,
        isLevelOfSpecificationAutomation,
    ])

    const commonIconColor = React.useMemo(() => {
        if (isLevelOfSpecificationAutomation && isHidden) {
            return defaultOOFColor
        } else if (disableArticleExpandInFullView) {
            return defaultOOFColor
        } else if (isPremiumPlus) {
            return !selectedProjectNode?.isInProject
                ? manufacturerSecondaryColorOOFColor
                : manufacturerSecondaryColor
        }
        return !selectedProjectNode?.isInProject
            ? defaultOOFColor
            : defaultColor
    }, [
        isLevelOfSpecificationAutomation,
        isPremiumPlus,
        selectedProjectNode?.isInProject,
        isHidden,
    ])

    const addIconColor = React.useMemo(() => {
        if (
            commonIconColor === defaultColor &&
            !!productTypeInFamilyViewContentType &&
            !isProductGroupIconShown(productTypeInFamilyViewContentType) &&
            productTypeInFamilyViewContentType !==
                ProductTypeInFamilyViewContentType.ARTICLEPRODUCTTYPE
        ) {
            // Set add icon color to disabled.
            return defaultOOFColor
        }
        return commonIconColor
    }, [commonIconColor, productTypeInFamilyViewContentType])

    const handleMainMenuDisplay = React.useCallback(() => {
        if (isLevelOfSpecificationAutomation && isHidden && isBPMenuDisplayed)
            return
        setIsMainMenuDisplayed((past) => !past)
    }, [
        isLevelOfSpecificationAutomation,
        isHidden,
        isBPMenuDisplayed,
        isMainMenuDisplayed,
    ])

    const handleAddIconClick = React.useCallback(() => {
        if (
            (!contentTeamFromStudioPage && isUnderProductGroupArticle) ||
            (isLevelOfSpecificationAutomation && isHidden)
        )
            return
        if (!contentTeamFromStudioPage) onClickAdd?.()
        if (
            contentTeamFromStudioPage &&
            isDraftMode &&
            (isSystemUser || isOMUser) &&
            (!isAdmin || isOMUser)
        ) {
            onClickAdd?.()
        }
    }, [
        isOMUser,
        contentTeamFromStudioPage,
        isDraftMode,
        isSystemUser,
        isAdmin,
        onClickAdd,
        isHidden,
    ])

    const isVisibilityIconVisible = React.useMemo(
        () =>
            !contentTeamFromStudioPage ||
            (contentTeamFromStudioPage &&
                isOMUser &&
                selectedProjectNode?.elementType ===
                    ElementType.PRODUCTFAMILY) ||
            (contentTeamFromStudioPage &&
                isOMUser &&
                selectedProjectNode?.elementType === ElementType.PRODUCTTYPE &&
                !template?.isProductType &&
                !template?.isProductGroup &&
                !template?.isForProductType),
        [contentTeamFromStudioPage, isOMUser, selectedProjectNode?.elementType, template]
    )

    const isDeleteIconVisible = React.useMemo(() => {
        if (contentTeamFromStudioPage) {
            if (
                (isAdmin && !isOMUser) ||
                isReviewer ||
                (isOMUser && !template?.isUserAdded)
            )
                return false
            if (
                selectedProjectNode?.elementType === 'PRODUCTFAMILY' ||
                (!hovered && !isRemoveHidden) ||
                (hovered && !template?.isBOD && !template?.isManufacturers)
            ) {
                return true
            }
        } else {
            if (!hovered) {
                if (
                    !isRemoveHidden &&
                    !template?.isProductType &&
                    isPropertyUserAdded
                )
                    return true
            } else {
                if (
                    isPropertyUserAdded &&
                    !isRemoveHidden &&
                    !template?.isBOD &&
                    !template?.isManufacturers &&
                    !template?.isProductType
                )
                    return true
            }
        }
    }, [
        contentTeamFromStudioPage,
        isAdmin,
        isOMUser,
        isReviewer,
        isRemoveHidden,
        isPropertyUserAdded,
        template?.isProductType,
        template?.isBOD,
        template?.isManufacturers,
        hovered,
        selectedProjectNode?.elementType,
    ])

    React.useEffect(() => {
        if (!hovered) {
            if (isNotesMenuDisplayed) {
                setIsNotesMenuDisplayed(false)
            } else if (!isBPMenuDisplayed && isMainMenuDisplayed) {
                setIsMainMenuDisplayed(false)
            }
        }
    }, [hovered, isMainMenuDisplayed, isNotesMenuDisplayed, isBPMenuDisplayed])
    return (
        <>
            <div
                data-testid="dvTemplateHovered"
                className={clsx(
                    className,
                    hovered || isPillsDialogOpen ? 'hovered' : 'unhovered',
                    classes.container
                )}
                id={id}
            >
                <GridContainer
                    className={classes.fullHeight}
                    alignContent="space-around"
                    alignItems="center"
                    justifyContent="flex-end"
                    direction="row"
                    ref={actionRef}
                >
                    {/* Displaying BP icons only for Articles */}
                    {template?.contentItem?.textType === 'Article' &&
                        !isBestPracticeViewDisabled &&
                        (isBestPractice || isGlobalBestPractice) && (
                            <GridItem className={clsx(classes.BPIcons)}>
                                {processEnv.REACT_APP_EnableSmartProjectCopy ===
                                    '1' && articleLoaded ? (
                                    <BestPracticesIcons
                                        isBestPractice={isBestPractice}
                                        isGlobalBestPractice={
                                            isGlobalBestPractice
                                        }
                                        isBestPracticeOutOfSync={
                                            isBestPracticeOutOfSync
                                        }
                                        isGlobalBestPracticeOutOfSync={
                                            isGlobalBestPracticeOutOfSync
                                        }
                                        isBestPracticeSyncing={
                                            isBestPracticeSyncing
                                        }
                                        isGlobalBestPracticeSyncing={
                                            isGlobalBestPracticeSyncing
                                        }
                                        isBestPracticeSynced={
                                            isBestPracticeSynced
                                        }
                                        isGlobalBestPracticeSynced={
                                            isGlobalBestPracticeSynced
                                        }
                                        handleOutOfSyncIconClick={
                                            handleOutOfSyncIconClick
                                        }
                                    />
                                ) : (
                                    <BestPracticesIcons
                                        isBestPractice={isBestPractice}
                                        isGlobalBestPractice={
                                            isGlobalBestPractice
                                        }
                                    />
                                )}
                            </GridItem>
                        )}
                    {/* item_action_circle_dots_brown; add brown to equivalent icon  */}
                    {hasTitle && !hovered && !isPillsDialogOpen && (
                        <>
                            {contentTeamFromStudioPage &&
                                !showSMMActionButtons && (
                                    <GridItem className={classes.gridItem}>
                                        {isHidden ? (
                                            <AddCommentSharp
                                                className={
                                                    classes.iconOutlinedTitle
                                                }
                                                htmlColor={
                                                    contentTeamFromStudioPage &&
                                                    !isReviewer &&
                                                    isDraftMode &&
                                                    !disableArticleExpandInFullView
                                                        ? defaultColor
                                                        : defaultOOFColor
                                                }
                                                fontSize="inherit"
                                            />
                                        ) : (
                                            <AddCommentOutlined
                                                className={
                                                    classes.iconOutlinedTitle
                                                }
                                                htmlColor={
                                                    contentTeamFromStudioPage &&
                                                    !isReviewer &&
                                                    isDraftMode &&
                                                    !disableArticleExpandInFullView
                                                        ? defaultColor
                                                        : defaultOOFColor
                                                }
                                                fontSize="inherit"
                                            />
                                        )}
                                    </GridItem>
                                )}
                            {isVisibilityIconVisible && (
                                <GridItem className={classes.gridItem}>
                                    {isHidden ? (
                                        <HoverTooltip text={'Activate Content'}>
                                            <VisibilityOffSharpIcon
                                                className={
                                                    classes.iconOutlinedTitle
                                                }
                                                htmlColor={eyeIconColor}
                                                fontSize="inherit"
                                            />
                                        </HoverTooltip>
                                    ) : (
                                        <HoverTooltip
                                            text={'Inactivate Content'}
                                        >
                                            <VisibilityOutlinedIcon
                                                className={
                                                    classes.iconOutlinedTitle
                                                }
                                                htmlColor={
                                                    disableProductTypeInactivation
                                                        ? defaultOOFColor
                                                        : activeEyeColor
                                                }
                                                fontSize="inherit"
                                            />
                                        </HoverTooltip>
                                    )}
                                </GridItem>
                            )}
                            <GridItem className={classes.gridItem}>
                                <HoverTooltip text={'Add Content'}>
                                    <AddIcon
                                        data-testid="addButton"
                                        htmlColor={addIconColor}
                                        className={clsx(
                                            onClickAdd &&
                                                !isHidden &&
                                                classes.addButton,
                                            selectedProjectNode?.isInProject
                                                ? isHidden ||
                                                  !onClickAdd ||
                                                  (isLevelOfSpecificationAutomation &&
                                                      isHidden)
                                                    ? classes.addIconInactiveTitle
                                                    : classes.addIconTitle
                                                : classes.addIconInactiveTitle
                                        )}
                                        onClick={handleAddIconClick}
                                        fontSize="inherit"
                                    />
                                </HoverTooltip>
                            </GridItem>
                            {isDeleteIconVisible && (
                                <GridItem className={classes.gridItem}>
                                    {isElementRemoved ? (
                                        <CircularProgress
                                            size={15}
                                            className={
                                                classes.newPropertyLoadingNonHover
                                            }
                                        />
                                    ) : (
                                        <HoverTooltip text={'Remove Content'}>
                                            <DeleteOutlineIcon
                                                data-testid="deleteButton"
                                                htmlColor={
                                                    isDeleteDisabled
                                                        ? defaultOOFColor
                                                        : commonIconColor
                                                }
                                                className={
                                                    selectedProjectNode?.isInProject
                                                        ? isHidden ||
                                                          isDeleteDisabled ||
                                                          contentTeamFromStudioPage
                                                            ? classes.trashCanIconInactiveTitle
                                                            : classes.trashCanIconTitle
                                                        : classes.trashCanIconInactiveTitle
                                                }
                                                fontSize="inherit"
                                            />
                                        </HoverTooltip>
                                    )}
                                </GridItem>
                            )}
                            {!selectedProjectNode?.isProjectOnly && (
                                <GridItem className={classes.gridItem}>
                                    <MoreVert
                                        onClick={
                                            (contentTeamFromStudioPage &&
                                                !isDraftMode) ||
                                            selectedProjectNode?.isInProject
                                                ? handleMainMenuDisplay
                                                : undefined
                                        }
                                        fontSize="inherit"
                                        htmlColor={commonIconColor}
                                        data-testid="moreMenu"
                                        className={clsx(
                                            contentTeamFromStudioPage &&
                                                !isDraftMode &&
                                                classes.bpPostionisContentTeamUser,
                                            (selectedProjectNode?.isInProject ||
                                                contentTeamFromStudioPage) &&
                                                classes.bpCursor
                                        )}
                                    />
                                </GridItem>
                            )}
                        </>
                    )}
                    {(hovered || isPillsDialogOpen) && (
                        <>
                            {contentTeamFromStudioPage &&
                                !showSMMActionButtons && (
                                    <GridItem className={classes.gridItem}>
                                        {isHidden ? (
                                            <AddCommentSharp
                                                className={
                                                    contentTeamFromStudioPage &&
                                                    isDraftMode &&
                                                    !isReviewer
                                                        ? classes.iconSharp
                                                        : classes.iconsInactive
                                                }
                                                fontSize="inherit"
                                            />
                                        ) : (
                                            <AddCommentOutlined
                                                data-testid="addNote-OutlinedIcon"
                                                htmlColor={
                                                    contentTeamFromStudioPage &&
                                                    !isReviewer &&
                                                    isDraftMode
                                                        ? defaultColor
                                                        : defaultOOFColor
                                                }
                                                className={
                                                    contentTeamFromStudioPage &&
                                                    !isReviewer &&
                                                    isDraftMode
                                                        ? classes.iconSharp
                                                        : classes.iconsInactive
                                                }
                                                onClick={
                                                    handleClickAddSpecifierNote
                                                }
                                                fontSize="inherit"
                                            />
                                        )}
                                    </GridItem>
                                )}
                            {isVisibilityIconVisible && (
                                <GridItem className={classes.gridItem}>
                                    {isHidden ? (
                                        <HoverTooltip text={'Activate Content'}>
                                            <VisibilityOffSharpIcon
                                                onClick={
                                                    checkActionPrerequisites
                                                }
                                                htmlColor={eyeIconColor}
                                                className={
                                                    isLevelOfSpecificationAutomation ||
                                                    selectedProjectNode?.isInProject
                                                        ? classes.iconSharp
                                                        : classes.iconsInactive
                                                }
                                                fontSize="inherit"
                                                data-testid="visibilityOffIcon"
                                            />
                                        </HoverTooltip>
                                    ) : (
                                        <HoverTooltip
                                            text={'Inactivate Content'}
                                        >
                                            <VisibilityOutlinedIcon
                                                data-testid="VisibilityOutlinedIcon"
                                                htmlColor={activeEyeColor}
                                                className={
                                                    disableProductTypeInactivation ||
                                                    !selectedProjectNode?.isInProject
                                                        ? classes.iconsInactive
                                                        : classes.iconSharp
                                                }
                                                onClick={
                                                    checkActionPrerequisites
                                                }
                                                fontSize="inherit"
                                            />
                                        </HoverTooltip>
                                    )}
                                </GridItem>
                            )}
                            <ConfirmationDialog
                                openDialog={showClashDialog}
                                dialogType="warning"
                                handleDialogClose={closeClashDialog}
                                dialogActionCallback={handleElementDeactivation}
                                dialogHeading="Clash Detected!"
                                confirmText="Yes"
                                contentNode={clashDialogContent}
                                size="large"
                            />
                            {!hasTitle && !contentTeamFromStudioPage && (
                                <GridItem className={classes.gridItem}>
                                    <HoverTooltip text={'Manage Options'}>
                                        <CachedIcon
                                            data-testid="cachedButton"
                                            className={clsx(
                                                !isHidden &&
                                                    !!onClickPills &&
                                                    classes.addButton,
                                                selectedProjectNode?.isInProject
                                                    ? isHidden || !onClickPills
                                                        ? classes.addIconInactive
                                                        : classes.addIcon
                                                    : classes.addIconInactive
                                            )}
                                            onClick={() =>
                                                onClickPills?.(template?.itemId)
                                            }
                                        />
                                    </HoverTooltip>
                                </GridItem>
                            )}
                            <GridItem className={classes.gridItem}>
                                <HoverTooltip text={'Add Content'}>
                                    <AddIcon
                                        data-testid="addButton"
                                        className={clsx(
                                            onClickAdd &&
                                                !isHidden &&
                                                classes.addButton,
                                            selectedProjectNode?.isInProject
                                                ? isHidden ||
                                                  !onClickAdd ||
                                                  (contentTeamFromStudioPage &&
                                                      !isDraftMode) ||
                                                  (isLevelOfSpecificationAutomation &&
                                                      isHidden)
                                                    ? classes.addIconInactive
                                                    : classes.addIcon
                                                : classes.addIconInactive
                                        )}
                                        onClick={handleAddIconClick}
                                        fontSize="inherit"
                                    />
                                </HoverTooltip>
                            </GridItem>
                            {isDeleteIconVisible && (
                                <GridItem className={classes.gridItem}>
                                    {isElementRemoved ? (
                                        <CircularProgress
                                            size={15}
                                            className={
                                                classes.newPropertyLoading
                                            }
                                        />
                                    ) : (
                                        <HoverTooltip text={'Remove Content'}>
                                            <DeleteOutlineIcon
                                                data-testid="deleteButton"
                                                className={
                                                    selectedProjectNode?.isInProject
                                                        ? isHidden ||
                                                          isDeleteDisabled ||
                                                          !deleteFamilyContent ||
                                                          (contentTeamFromStudioPage &&
                                                              !isDraftMode)
                                                            ? classes.trashCanIconInactive
                                                            : classes.trashCanIcon
                                                        : classes.trashCanIconInactive
                                                }
                                                onClick={
                                                    !isDeleteDisabled &&
                                                    !isDraftMode
                                                        ? handleDialogOpen
                                                        : deleteFamilyContent
                                                }
                                                fontSize="inherit"
                                            />
                                        </HoverTooltip>
                                    )}
                                </GridItem>
                            )}
                            {!selectedProjectNode?.isProjectOnly &&
                                template?.type?.toLowerCase() !== 'table' && (
                                    <GridItem className={classes.gridItem}>
                                        <MoreVert
                                            onClick={
                                                (contentTeamFromStudioPage &&
                                                    !isDraftMode) ||
                                                (selectedProjectNode?.isInProject &&
                                                    !template?.isDeactivated)
                                                    ? handleMainMenuDisplay
                                                    : undefined
                                            }
                                            fontSize="inherit"
                                            data-testid="moreMenu"
                                            className={clsx(
                                                isLevelOfSpecificationAutomation &&
                                                    isHidden
                                                    ? classes.moreMenuLevelOfSpecificationAutomationIcon
                                                    : classes.bpPosition,
                                                contentTeamFromStudioPage &&
                                                    !isDraftMode &&
                                                    classes.bpPostionisContentTeamUser,
                                                (selectedProjectNode?.isInProject ||
                                                    contentTeamFromStudioPage) &&
                                                    classes.bpCursor
                                            )}
                                        />
                                    </GridItem>
                                )}
                        </>
                    )}
                </GridContainer>
            </div>

            {isMainMenuDisplayed && (
                <WorkspacePopover
                    menuItems={
                        isNotesMenuDisplayed ? notesMenuItems : mainMenuItems
                    }
                    isActionPopoverDisplayed={isMainMenuDisplayed}
                    handleActionPopoverClick={handleMainMenuDisplay}
                    anchorElement={actionRef.current ? actionRef.current : null}
                    anchorOrigin={{
                        vertical: isBPMenuDisplayed
                            ? 'top'
                            : (actionRef?.current?.clientHeight ?? 0) +
                              (isNotesMenuDisplayed ? 80 : 60),
                        horizontal:
                            (actionRef?.current?.clientWidth ?? 0) -
                            (isBPMenuDisplayed ? -5 : 5),
                    }}
                    customTextStyle={classes.notesMenuText}
                    transformOrigin={
                        isBPMenuDisplayed
                            ? { vertical: 'top', horizontal: 'right' }
                            : undefined
                    }
                />
            )}
            {isSustainabilityWarningDisplayed && (
                <SustainabilityWarningDialog
                    isDialogOpen={isSustainabilityWarningDisplayed}
                    cancelCallback={cancelAction}
                    handleElementDeactivation={handleElementDeactivation}
                />
            )}
            <ProductTypeDeactivationWarning
                openDialog={isProductTypeDeactivationDialogOpen}
                handleDialogClose={closeProductDeactivationDialog}
            />
        </>
    )
}
